import { Sparkles, Star, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";

// Static config objects
const SESSION_MODE_ICONS = { regular: "💬", escalation: "🎮", practice: "🎯" };
const SESSION_MODE_COLORS = { 
  regular: "text-white/40", 
  escalation: "text-emerald-400", 
  practice: "text-cyan-400" 
};
const SESSION_CONTEXT_CONFIG = {
  "persona_chat": { label: "Persona", color: "bg-divine-gold/20 text-divine-gold border-divine-gold/30" },
  "advice_direct": { label: "Advice", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
  "advice_coaching": { label: "Coaching", color: "bg-cyan-600/20 text-cyan-300 border-cyan-600/30" },
  "social": { label: "Social", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  "dating_bio": { label: "Bio Writer", color: "bg-pink-500/20 text-pink-400 border-pink-500/30" },
  "dating_img": { label: "Photo Review", color: "bg-rose-500/20 text-rose-400 border-rose-500/30" },
  "advice": { label: "Advice", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
  "coaching_session": { label: "🏹 Coach", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
};
const SESSION_BORDER_COLOR_MAP = {
  "persona_chat": "hover:border-divine-gold/40",
  "advice_direct": "hover:border-cyan-500/40",
  "advice_coaching": "hover:border-cyan-600/40",
  "social": "hover:border-purple-500/40",
  "dating_bio": "hover:border-pink-500/40",
  "dating_img": "hover:border-rose-500/40",
  "advice": "hover:border-cyan-500/40",
  "coaching_session": "hover:border-amber-500/40",
};

export const SessionList = ({
  sessions,
  loadingSessions,
  matchId,
  match,
  scenarioNameMap,
  onLoadSession,
  onNewConversation,
  onRenameSession,
  onDeleteSession,
  onToggleFavourite,
  // Mode selector props
  showModeSelector = false,
  modeSelectorStep = "mode",
  sortedPracticeScenarios = [],
  onModeSelect,
  onDifficultySelect,
  onScenarioSelect,
  onBackToModeSelection,
  onCloseModeSelector,
  // Advice mode props (for non-match chats)
  onAdviceModeSelect,
  onAdviceSubSelect,
  onDatingSubSelect,
  // Connection level for Regular mode description
  connectionLevel = null,
  connectionLevelName = null,
  // User persona name for Live mode
  userPersonaName = null,
}) => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* New Conversation Button */}
      <div className="mb-6">
        <Button
          onClick={onNewConversation}
          className="w-full py-8 text-lg rounded-2xl bg-gradient-to-r from-divine-gold/20 to-divine-gold/10 border border-divine-gold/30 hover:border-divine-gold/50 text-divine-gold"
          data-testid="new-conversation-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          {matchId ? `New Conversation with ${match?.name || "Persona"}` : "Start New Chat"}
        </Button>
        
        {/* Expanding Mode Selector Panel - appears beneath the button */}
        {showModeSelector && (
          <div className="mt-4 p-4 bg-divine-charcoal/80 rounded-xl border border-white/20" data-testid="mode-selector-panel">
            
            {/* PERSONA CHAT MODES (when matchId exists) */}
            {matchId && modeSelectorStep === "mode" && (
              <>
                <h3 className="text-white font-medium mb-3 text-center">Choose Session Mode</h3>
                <div className="grid grid-cols-3 gap-3">
                  {/* Regular Chat - GREEN */}
                  <button
                    onClick={() => onModeSelect("regular")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-green-500/30 hover:border-green-500/60 bg-green-500/10 hover:bg-green-500/20 transition-all"
                    data-testid="mode-regular"
                  >
                    <span className="text-2xl">💬</span>
                    <span className="text-green-400 font-medium">Regular</span>
                    <span className="text-green-400/60 text-xs text-center">
                      Free-flowing simulated conversation{connectionLevel ? ` at your Level ${connectionLevel}${connectionLevelName ? `: ${connectionLevelName}` : ''}` : ''}
                    </span>
                  </button>
                  {/* Escalation - YELLOW */}
                  <button
                    onClick={() => onModeSelect("escalation")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-yellow-500/30 hover:border-yellow-500/60 bg-yellow-500/10 hover:bg-yellow-500/20 transition-all"
                    data-testid="mode-escalation"
                  >
                    <span className="text-2xl">🎮</span>
                    <span className="text-yellow-400 font-medium">Escalation</span>
                    <span className="text-yellow-400/60 text-xs text-center">Guided progression through 13 levels — First Spark to Union</span>
                  </button>
                  {/* Practice - BLUE */}
                  <button
                    onClick={() => onModeSelect("practice")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-blue-500/30 hover:border-blue-500/60 bg-blue-500/10 hover:bg-blue-500/20 transition-all"
                    data-testid="mode-practice"
                  >
                    <span className="text-2xl">🎯</span>
                    <span className="text-blue-400 font-medium">Practice</span>
                    <span className="text-blue-400/60 text-xs text-center">Scenario-based coaching through roleplay exercises</span>
                  </button>
                </div>
                
                {/* Live Mode - Purple - Coming Soon */}
                <div className="mt-4">
                  <button
                    className="w-full p-4 rounded-lg border border-purple-500/30 bg-purple-500/10 opacity-70 cursor-not-allowed transition-all"
                    data-testid="mode-live"
                    disabled
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-purple-400 font-medium">Get Live Advice</span>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl">💖✨</span>
                        <span className="text-purple-300/80 text-xs text-center">
                          Track real conversation exchange between {userPersonaName || 'you'} and {match?.name || 'your match'} to get live guidance from Agent Eros. <span className="text-purple-400/60">(coming soon)</span>
                        </span>
                        <span className="text-2xl">✨💖</span>
                      </div>
                    </div>
                  </button>
                </div>
              </>
            )}
            
            {/* EROS CHAT MODES (when no matchId - advice mode) */}
            {!matchId && modeSelectorStep === "mode" && (
              <>
                <h3 className="text-white font-medium mb-3 text-center">What would you like help with?</h3>
                <div className="grid grid-cols-3 gap-3">
                  {/* Advice - CYAN */}
                  <button
                    onClick={() => onAdviceModeSelect?.("advice")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-cyan-500/30 hover:border-cyan-500/60 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all"
                    data-testid="mode-advice"
                  >
                    <span className="text-2xl">💡</span>
                    <span className="text-cyan-400 font-medium">Dating Advice</span>
                    <span className="text-cyan-400/60 text-xs text-center">Get personalized coaching and tips</span>
                  </button>
                  {/* Social - PURPLE */}
                  <button
                    onClick={() => onAdviceModeSelect?.("social")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-purple-500/30 hover:border-purple-500/60 bg-purple-500/10 hover:bg-purple-500/20 transition-all"
                    data-testid="mode-social"
                  >
                    <span className="text-2xl">🌐</span>
                    <span className="text-purple-400 font-medium">Social Skills</span>
                    <span className="text-purple-400/60 text-xs text-center">Improve conversation & social skills</span>
                  </button>
                  {/* Dating Tools - PINK */}
                  <button
                    onClick={() => onAdviceModeSelect?.("dating")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-pink-500/30 hover:border-pink-500/60 bg-pink-500/10 hover:bg-pink-500/20 transition-all"
                    data-testid="mode-dating"
                  >
                    <span className="text-2xl">✨</span>
                    <span className="text-pink-400 font-medium">Dating Tools</span>
                    <span className="text-pink-400/60 text-xs text-center">Bio writing & photo review</span>
                  </button>
                </div>
              </>
            )}
            
            {/* Advice Sub-Options (Direct vs Coaching) */}
            {!matchId && modeSelectorStep === "advice_sub" && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={onBackToModeSelection}
                    className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1"
                  >
                    ← Back
                  </button>
                  <h3 className="text-cyan-400 font-medium">Dating Advice Style</h3>
                  <div className="w-12"></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => onAdviceSubSelect?.("direct")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-cyan-500/30 hover:border-cyan-500/60 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all"
                  >
                    <span className="text-2xl">💬</span>
                    <span className="text-cyan-400 font-medium">Direct Advice</span>
                    <span className="text-cyan-400/60 text-xs text-center">Quick tips and answers</span>
                  </button>
                  <button
                    onClick={() => onAdviceSubSelect?.("coaching")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-amber-500/30 hover:border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 transition-all"
                  >
                    <span className="text-2xl">🏹</span>
                    <span className="text-amber-400 font-medium">Coaching Mode</span>
                    <span className="text-amber-400/60 text-xs text-center">In-depth guidance with follow-ups</span>
                  </button>
                </div>
              </>
            )}
            
            {/* Dating Tools Sub-Options (Bio vs Photo) */}
            {!matchId && modeSelectorStep === "dating_sub" && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={onBackToModeSelection}
                    className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1"
                  >
                    ← Back
                  </button>
                  <h3 className="text-pink-400 font-medium">Dating Tools</h3>
                  <div className="w-12"></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => onDatingSubSelect?.("bio")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-pink-500/30 hover:border-pink-500/60 bg-pink-500/10 hover:bg-pink-500/20 transition-all"
                  >
                    <span className="text-2xl">✍️</span>
                    <span className="text-pink-400 font-medium">Bio Writer</span>
                    <span className="text-pink-400/60 text-xs text-center">Craft the perfect dating profile</span>
                  </button>
                  <button
                    onClick={() => onDatingSubSelect?.("img")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-rose-500/30 hover:border-rose-500/60 bg-rose-500/10 hover:bg-rose-500/20 transition-all"
                  >
                    <span className="text-2xl">📸</span>
                    <span className="text-rose-400 font-medium">Photo Review</span>
                    <span className="text-rose-400/60 text-xs text-center">Get feedback on your photos</span>
                  </button>
                </div>
              </>
            )}

            {/* Step 2a: Difficulty Selection (for Escalation) */}
            {modeSelectorStep === "difficulty" && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={onBackToModeSelection}
                    className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1"
                  >
                    ← Back
                  </button>
                  <h3 className="text-emerald-400 font-medium text-center flex items-center gap-2">
                    <span>🎮</span> Select Difficulty
                  </h3>
                  <div className="w-12"></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => onDifficultySelect("easy")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-green-500/30 hover:border-green-500/60 bg-green-500/10 hover:bg-green-500/20 transition-all"
                    data-testid="difficulty-easy"
                  >
                    <span className="text-2xl">⚡</span>
                    <span className="text-green-400 font-medium">Easy</span>
                    <span className="text-green-400/60 text-xs text-center">+3 XP Bonus</span>
                  </button>
                  <button
                    onClick={() => onDifficultySelect("medium")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-yellow-500/30 hover:border-yellow-500/60 bg-yellow-500/10 hover:bg-yellow-500/20 transition-all"
                    data-testid="difficulty-medium"
                  >
                    <span className="text-2xl">🌱</span>
                    <span className="text-yellow-400 font-medium">Medium</span>
                    <span className="text-yellow-400/60 text-xs text-center">+1 XP Bonus</span>
                  </button>
                  <button
                    onClick={() => onDifficultySelect("hard")}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-red-500/30 hover:border-red-500/60 bg-red-500/10 hover:bg-red-500/20 transition-all"
                    data-testid="difficulty-hard"
                  >
                    <span className="text-2xl">🔥</span>
                    <span className="text-red-400 font-medium">Hard</span>
                    <span className="text-red-400/60 text-xs text-center">+0 XP Bonus</span>
                  </button>
                </div>
              </>
            )}

            {/* Step 2b: Scenario Selection (for Practice) */}
            {modeSelectorStep === "scenario" && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={onBackToModeSelection}
                    className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1"
                  >
                    ← Back
                  </button>
                  <h3 className="text-cyan-400 font-medium text-center flex items-center gap-2">
                    <span>🎯</span> Select Scenario
                  </h3>
                  <div className="w-12"></div>
                </div>
                <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                  {sortedPracticeScenarios.map((scenario) => {
                    const difficultyColors = {
                      easy: {
                        border: "border-green-500/30 hover:border-green-500/60",
                        bg: "bg-green-500/10 hover:bg-green-500/20",
                        text: "text-green-400",
                        textMuted: "text-green-400/60",
                        badge: "bg-green-500/20 text-green-400",
                        icon: "⚡"
                      },
                      medium: {
                        border: "border-yellow-500/30 hover:border-yellow-500/60",
                        bg: "bg-yellow-500/10 hover:bg-yellow-500/20",
                        text: "text-yellow-400",
                        textMuted: "text-yellow-400/60",
                        badge: "bg-yellow-500/20 text-yellow-400",
                        icon: "🌱"
                      },
                      hard: {
                        border: "border-red-500/30 hover:border-red-500/60",
                        bg: "bg-red-500/10 hover:bg-red-500/20",
                        text: "text-red-400",
                        textMuted: "text-red-400/60",
                        badge: "bg-red-500/20 text-red-400",
                        icon: "🔥"
                      }
                    };
                    const colors = difficultyColors[scenario.difficulty] || difficultyColors.medium;
                    
                    return (
                      <button
                        key={scenario.scenario_id}
                        onClick={() => onScenarioSelect(scenario.scenario_id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border ${colors.border} ${colors.bg} transition-all`}
                        data-testid={`scenario-${scenario.scenario_id}`}
                      >
                        <span className="text-xl">{scenario.icon || "📝"}</span>
                        <span className={`${colors.text} font-medium text-sm`}>{scenario.name}</span>
                        <span className={`${colors.textMuted} text-[10px] text-center line-clamp-2`}>{scenario.description}</span>
                        <span className={`${colors.badge} text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1`}>
                          <span>{colors.icon}</span>
                          <span className="capitalize">{scenario.difficulty}</span>
                        </span>
                      </button>
                    );
                  })}
                  {sortedPracticeScenarios.length === 0 && (
                    <div className="col-span-2 text-center py-4 text-white/40">
                      No scenarios available
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Session List */}
      <div className="space-y-3">
        {loadingSessions ? (
          <div className="text-center py-8 text-white/40">Loading conversations...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">{matchId ? "No conversations yet" : "Start your journey with Agent Eros"}</p>
            <p className="text-white/30 text-sm mt-2">Start a new conversation above</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-white/40 text-sm mb-3">Previous Conversations</p>
            {sessions.map((session) => {
              const sessionMode = session.mode || "regular";
              const scenarioName = session.scenario_id ? scenarioNameMap[session.scenario_id] : null;
              const sessionContext = session.context || "advice";
              const contextStyle = SESSION_CONTEXT_CONFIG[sessionContext] || SESSION_CONTEXT_CONFIG["advice"];
              const hoverBorder = SESSION_BORDER_COLOR_MAP[sessionContext] || "hover:border-divine-gold/40";
              
              return (
                <div
                  key={session.session_id}
                  onClick={() => onLoadSession(session.session_id)}
                  className={`p-4 rounded-xl cursor-pointer transition-all group bg-white/5 hover:bg-white/10 border border-white/10 ${hoverBorder}`}
                  data-testid={`session-item-${session.session_id}`}
                >
                  {/* Row 1: Session title with mode icon and badges */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={SESSION_MODE_COLORS[sessionMode]}>{SESSION_MODE_ICONS[sessionMode]}</span>
                    {session.is_favourite && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />}
                    {/* Context Type Badge */}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${contextStyle.color}`}>
                      {contextStyle.label}
                    </span>
                    <h3 className="text-white font-medium truncate flex-1">{session.title}</h3>
                    
                    {/* Desktop Action Icons - inline */}
                    <div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => onRenameSession(session.session_id, e)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-all"
                        title="Rename"
                      >
                        <Pencil className="w-3.5 h-3.5 text-white/60 hover:text-white" />
                      </button>
                      <button
                        onClick={(e) => onToggleFavourite(session.session_id, e)}
                        className="p-1.5 hover:bg-yellow-500/20 rounded-lg transition-all"
                        title={session.is_favourite ? "Remove from favourites" : "Add to favourites"}
                      >
                        <Star className={`w-3.5 h-3.5 ${session.is_favourite ? "text-yellow-400 fill-yellow-400" : "text-white/60 hover:text-yellow-400"}`} />
                      </button>
                      <button
                        onClick={(e) => onDeleteSession(session.session_id, e)}
                        className="p-1.5 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white/60 hover:text-red-400" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Row 2: Date and message count */}
                  <p className="text-white/40 text-xs mt-1">
                    {new Date(session.updated_at).toLocaleDateString()} • {session.message_count || 0} messages
                    {sessionMode !== "regular" && (
                      <span className={`ml-2 ${SESSION_MODE_COLORS[sessionMode]}`}>
                        {sessionMode === "escalation" && session.difficulty && ` • ${session.difficulty}`}
                        {sessionMode === "practice" && scenarioName && ` • ${scenarioName}`}
                      </span>
                    )}
                    {/* Coaching Session Link - From match session to coaching session */}
                    {session.linked_coaching_session_id && (
                      <span 
                        className="ml-2 text-divine-gold hover:text-divine-gold/80 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to coaching session in Eros Chat
                          window.location.href = `/chat?coaching_session=${session.linked_coaching_session_id}`;
                        }}
                        title="View linked coaching session"
                      >
                        (🏹EROS)
                      </span>
                    )}
                    {/* Reverse Link - From coaching session to match session */}
                    {session.context === "coaching_session" && session.linked_chat_session_id && session.match_id && (
                      <span 
                        className="ml-2 text-amber-400 hover:text-amber-300 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to match chat session
                          window.location.href = `/chat/${session.match_id}?session=${session.linked_chat_session_id}`;
                        }}
                        title="View linked match conversation"
                      >
                        (🏹EROS)
                      </span>
                    )}
                  </p>
                  
                  {/* Row 3: Mobile Action Icons - always visible, right aligned */}
                  <div className="md:hidden flex items-center justify-end gap-2 mt-2 pt-2 border-t border-white/5">
                    <button
                      onClick={(e) => onRenameSession(session.session_id, e)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-all"
                      title="Rename"
                    >
                      <Pencil className="w-4 h-4 text-white/60 hover:text-white" />
                    </button>
                    <button
                      onClick={(e) => onToggleFavourite(session.session_id, e)}
                      className="p-2 hover:bg-yellow-500/20 rounded-lg transition-all"
                      title={session.is_favourite ? "Remove from favourites" : "Add to favourites"}
                    >
                      <Star className={`w-4 h-4 ${session.is_favourite ? "text-yellow-400 fill-yellow-400" : "text-white/60 hover:text-yellow-400"}`} />
                    </button>
                    <button
                      onClick={(e) => onDeleteSession(session.session_id, e)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-white/60 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export { SESSION_MODE_ICONS, SESSION_MODE_COLORS, SESSION_CONTEXT_CONFIG, SESSION_BORDER_COLOR_MAP };
export default SessionList;
